import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./components/AuthContext";
import RoutePageViews from "./analytics/RoutePageViews";
import CookieConsent from "./components/CookieConsent";
import { LangProvider } from "./components/LangContext";
import { api } from "./lib/api";
import Landing from "./pages/Landing";
import Reportage from "./pages/Reportage";
import News from "./pages/News";
import Verktyg from "./pages/Verktyg";
import PostDetail from "./pages/PostDetail";
import Admin from "./pages/Admin";

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    api
      .post("/auth/session", { session_id: sessionId })
      .then((r) => {
        setUser(r.data); // sätt användaren i kontexten – lita inte enbart på router-state
        navigate("/redaktion", { state: { user: r.data }, replace: true });
      })
      .catch((err) =>
        navigate("/redaktion", {
          state: { authError: err.response?.data?.detail || "Inloggningen misslyckades" },
          replace: true,
        })
      );
  }, [location.hash, navigate, setUser]);

  return <div className="min-h-screen bg-[#0F1412]" data-testid="auth-callback-loading" />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/reportage" element={<Reportage />} />
      <Route path="/nyheter" element={<News />} />
      <Route path="/verktyg" element={<Verktyg />} />
      <Route path="/reportage/:slug" element={<PostDetail />} />
      <Route path="/redaktion" element={<Admin />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <div className="grain" aria-hidden="true" />
      <BrowserRouter>
        <RoutePageViews />
        <LangProvider>
          <AuthProvider>
            <AppRouter />
            <CookieConsent />
            <Toaster position="bottom-right" />
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}
